<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import Modal from "@/components/modals/ModalContainer.vue";
import { ArrowDown, LoaderCircle, Check, ExternalLink, ChevronDown } from "lucide-vue-next";
import { useWeb3AuthStore } from "~/stores/storeWeb3auth";
import { Currency } from "fungi-types";
import { formatNumberLocal } from "~/utils/math";
import { useDebounceFn } from "@vueuse/core";
const web3AuthStore = useWeb3AuthStore();

const { t } = useI18n();
const errorMessage = ref<any>();

const gasFee = ref("");

const swap = async () => {
  try {
    errorMessage.value = null;
    isLoading.value = true;
    if (swapOutSelected.value.id === Currency.USDC) {
      const txHash = await web3AuthStore.swapPolToUsdc(amountToSwap.value);
      amountToSwap.value = 0;
      transactionStatus.value = {
        success: true,
        txHash: txHash,
      };
      isLoading.value = false;
      return;
    }

    let action: "wrap" | "unwrap" = "wrap";
    if (swapInSelected.value.id === Currency.WPOL && swapOutSelected.value.id === Currency.POL) {
      action = "unwrap";
    } else if (
      swapInSelected.value.id === Currency.POL &&
      swapOutSelected.value.id === Currency.WPOL
    ) {
      action = "wrap";
    } else {
      throw new Error("Invalid swap action");
    }
    console.log(
      `Swapping ${amountToSwap.value} ${swapInSelected.value.label} to ${swapOutSelected.value.label} with action: ${action}`
    );
    const txHash = await web3AuthStore.swapPolWpol(amountToSwap.value, action);
    amountToSwap.value = 0;
    transactionStatus.value = {
      success: true,
      txHash: txHash,
    };
    isLoading.value = false;
  } catch (err) {
    console.error("Swap failed", err);
    errorMessage.value = err;
    isLoading.value = false;
  }
};

const emit = defineEmits(["onClose"]);

const handlOnClose = () => {
  emit("onClose");
};

const props = withDefaults(
  defineProps<{
    show: boolean;
  }>(),
  {
    show: false,
  }
);

const amountToSwap = ref(0);
const amountOutput = ref(0);
const isRotating = ref(false);

const isAmountValid = computed(() => {
  const balance = Number(userBalanceInSelectedCurrency.value);
  return amountToSwap.value <= balance;
});
const isLoading = ref(false);
const transactionStatus = ref({
  success: false,
  txHash: "",
});

const openExplorer = () => {
  const url = `https://polygonscan.com/tx/${transactionStatus.value.txHash}`;
  window.open(url, "_blank");
};

const reset = () => {
  amountToSwap.value = 0;
  transactionStatus.value = {
    success: false,
    txHash: "",
  };
};

const updateEstimation = async () => {
  if (amountToSwap.value > 0) {
    if (swapOutSelected.value.id === Currency.USDC) {
      debouncedGetPriceHistory();
    } else {
      amountOutput.value = amountToSwap.value;
      const estimatedGas = await web3AuthStore.estimateWrapUnWrapGasFast("wrap");
      gasFee.value = estimatedGas.gasFeeInPOL || "0";
    }
  } else {
    amountOutput.value = amountToSwap.value;
    gasFee.value = "0";
    quote.value = null;
  }
};

watch(amountToSwap, () => {
  updateEstimation();
});

type swapOptions = {
  id: Currency;
  value: Currency;
  label: string;
  img: string;
};

const swapInOptions: swapOptions[] = [
  {
    id: Currency.POL,
    value: Currency.POL,
    label: "POL",
    img: "/images/pol.svg",
  },
  {
    id: Currency.WPOL,
    value: Currency.WPOL,
    label: "WPOL",
    img: "/images/wpol.svg",
  },
];

const handleOnchangeInput = (selection: swapOptions) => {
  if (selection.id === Currency.POL) {
    swapOutSelected.value = {
      id: Currency.WPOL,
      value: Currency.WPOL,
      label: "WPOL",
      img: "/images/wpol.svg",
    };
    swapOutOptions.value = [
      {
        id: Currency.WPOL,
        value: Currency.WPOL,
        label: "WPOL",
        img: "/images/wpol.svg",
      },
      {
        id: Currency.USDC,
        value: Currency.USDC,
        label: "USDC",
        img: "/images/usdc.svg",
      },
    ];
  } else if (selection.id === Currency.WPOL) {
    swapOutSelected.value = {
      id: Currency.POL,
      value: Currency.POL,
      label: "POL",
      img: "/images/pol.svg",
    };
    swapOutOptions.value = [
      {
        id: Currency.POL,
        value: Currency.POL,
        label: "POL",
        img: "/images/pol.svg",
      },
    ];
  }
  swapInSelected.value = selection;
  updateEstimation();
};

const handleOnchangeOutput = (selection: swapOptions) => {
  swapOutSelected.value = selection;
  updateEstimation();
};

const swapInSelected = ref<swapOptions>(swapInOptions[0]);
const swapOutSelected = ref<swapOptions>({
  id: Currency.USDC,
  value: Currency.USDC,
  label: "USDC",
  img: "/images/usdc.svg",
});

const swapOutOptions = ref<swapOptions[]>([
  {
    id: Currency.USDC,
    value: Currency.USDC,
    label: "USDC",
    img: "/images/usdc.svg",
  },
  {
    id: Currency.POL,
    value: Currency.POL,
    label: "POL",
    img: "/images/pol.svg",
  },
]);

const userBalanceInSelectedCurrency = computed((): number | string => {
  switch (swapInSelected.value.value) {
    case Currency.POL:
      return Number(web3AuthStore.polBalance) - 1; // keep 1 POL for gas fees
    case Currency.WPOL:
      return web3AuthStore.wpolBalance;
    default:
      return 0;
  }
});

const userBalanceInSelectedOutputCurrency = computed((): string => {
  switch (swapOutSelected.value.value) {
    case Currency.POL:
      return web3AuthStore.polBalance;
    case Currency.WPOL:
      return web3AuthStore.wpolBalance;
    case Currency.USDC:
      return web3AuthStore.usdcBalance;
    default:
      return "0";
  }
});

const loadingQuote = ref(false);
const quote = ref<{
  amountOut: string;
  amountOutNumber: number;
  pricePerUsdc: number;
  pricePerPol: number;
} | null>(null);

const debouncedGetPriceHistory = useDebounceFn(async () => {
  try {
    loadingQuote.value = true;
    quote.value = await web3AuthStore.getSwapQuotePolToUsdc(amountToSwap.value);
    const gasFeeEstimate = await web3AuthStore.estimateSwapPolToUsdcGas(amountToSwap.value);
    gasFee.value = gasFeeEstimate.totalGasFee;
    amountOutput.value = quote.value.amountOutNumber;
  } catch (err) {
    console.error("Failed to fetch price history", err);
  } finally {
    loadingQuote.value = false;
  }
}, 1000);

const setMaxAmount = () => {
  amountToSwap.value = Number(userBalanceInSelectedCurrency.value);
};
</script>

<template>
  <Modal :show="show" @onClose="handlOnClose" title="Swap">
    <div
      v-if="isLoading || transactionStatus.success"
      class="bg-black/90 inset-0 absolute z-50 flex justify-center items-center"
    >
      <div v-if="isLoading" class="flex flex-col justify-center items-center gap-4 text-white">
        <LoaderCircle class="animate-spin" />
        Transaction en cours ...
      </div>
      <div
        v-if="transactionStatus.success"
        class="flex flex-col justify-center items-center gap-4 text-white"
      >
        <div class="bg-green-600 rounded-full p-2">
          <Check :stroke-width="4" />
        </div>
        <div>Transaction confirmée !</div>
        <div class="flex flex-col gap-2">
          <Button
            type="secondary"
            size="xs"
            @click="openExplorer"
            class="w-full !bg-white !text-black hover:!bg-black hover:!text-white"
          >
            <div class="flex gap-1 items-center justify-center">
              View on Explorer <ExternalLink class="h-4 mb-0.5" />
            </div>
          </Button>
          <Button type="secondary-outline" size="xs" @click="reset" class="w-full"> Close </Button>
        </div>
      </div>
    </div>
    <div class="flex flex-col justify-center items-center">
      <div class="bg-slate-800 w-full py-6 px-3 rounded-t-lg flex justify-between items-center">
        <input
          placeholder="0"
          type="number"
          v-model="amountToSwap"
          :disable="isLoading"
          class="w-full bg-transparent focus:outline-none text-4xl"
        />
        <div class="flex gap-1 flex-col justify-end items-end">
          <div>
            <InputSelect
              :selectedId="swapInSelected.id"
              @onChange="handleOnchangeInput"
              :options="swapInOptions"
              :rounded="false"
              position="top"
              class="position-relative z-20 group self-end w-fit"
            >
              <div
                class="flex gap-2 text-sm items-center p-2 px-3 bg-black hover:bg-black/50 rounded-full w-fit cursor-pointer"
              >
                <div>
                  <img :src="swapInSelected.img" class="min-w-5 mb-0.5" alt="POL" />
                </div>
                <div class="flex items-center gap-1">
                  {{ swapInSelected.label }} <ChevronDown class="w-5 opacity-50" />
                </div>
              </div>
            </InputSelect>
          </div>
          <div
            class="text-xs text-right text-neutral-400 whitespace-nowrap flex gap-1 items-center justify-end"
            :class="{
              'text-red-500': isAmountValid === false,
            }"
          >
            {{ formatNumberLocal(Number(userBalanceInSelectedCurrency)) }}
            {{ swapInSelected.label }}
            <span class="text-blue-500 hover:underline cursor-pointer" @click="setMaxAmount">
              Max.
            </span>
          </div>
        </div>
      </div>
      <div class="h-0.5 relative hover:cursor-pointer">
        <div
          :class="['arrow-container', { rotating: isRotating }]"
          class="rounded-full text-black w-10 h-10 flex justify-center items-center absolute left-1/2 -translate-x-1/2 -translate-y-1/2 border border-transparent hover:border-white transition-all duration-200"
        >
          <div
            class="w-9 h-9 flex justify-center items-center rounded-full bg-slate-300 border-black border-2"
          >
            <ArrowDown />
          </div>
        </div>
      </div>
      <div class="bg-slate-900 w-full py-6 px-3 rounded-b-lg flex justify-between items-center">
        <input
          placeholder="0"
          type="number"
          v-model="amountOutput"
          disabled
          class="w-full bg-transparent focus:outline-none text-4xl"
        />
        <div class="flex gap-1 flex-col">
          <InputSelect
            :selectedId="swapOutSelected.id"
            @onChange="handleOnchangeOutput"
            :options="swapOutOptions"
            :rounded="false"
            position="bottom"
            class="position-relative z-20 group"
          >
            <div class="flex gap-2 text-sm items-center p-2 px-3 bg-black rounded-full w-fit">
              <div>
                <img :src="swapOutSelected.img" class="min-w-5 mb-0.5" alt="POL" />
              </div>
              <div class="flex items-center gap-1">
                {{ swapOutSelected.label }} <ChevronDown class="w-5 opacity-50" />
              </div>
            </div>
          </InputSelect>
          <div class="text-xs text-right text-neutral-400">
            {{ formatNumberLocal(Number(userBalanceInSelectedOutputCurrency)) }}
            {{ swapOutSelected.label }}
          </div>
        </div>
      </div>
    </div>
    <div
      class="bg-neutral-800/60 p-3 rounded-lg text-xs mt-4 text-neutral-300 flex flex-col gap-3 relative"
    >
      <div
        v-if="loadingQuote"
        class="absolute inset-0 bg-black/70 flex justify-center items-center rounded-lg"
      ></div>
      <div class="flex justify-between">
        <div class="text-zinc-400">{{ t("_token-transfer.rate") }}</div>
        <div class="flex gap-1" v-if="swapOutSelected.id === Currency.USDC">
          <template v-if="quote">
            {{ quote?.pricePerPol.toFixed(6) }}
            <img class="rounded-full h-4" src="/images/usdc.svg" />
          </template>
          <div class="text-zinc-400" v-else></div>
        </div>
        <div v-else>1 POL = 1</div>
      </div>
      <div class="flex justify-between">
        <div class="text-zinc-400">{{ t("_token-transfer.gas-fee") }}</div>
        <div class="flex gap-1">
          <template v-if="Number(gasFee) > 0">
            {{ Number(gasFee).toFixed(6) }}
            <img class="rounded-full h-4" src="/images/pol-token.svg" />
          </template>
          <div class="text-zinc-400" v-else>-</div>
        </div>
      </div>
      <div class="flex gap-1 justify-between" v-if="swapOutSelected.id === Currency.USDC">
        <div class="text-zinc-400">Slippage</div>
        <div>0.5%</div>
      </div>
      <div class="flex gap-1 justify-between border-t border-zinc-600 pt-2">
        <div class="text-zinc-400">{{ t("_token-transfer.minimum-receive") }}</div>
        <div class="flex gap-1">
          <template v-if="quote && swapOutSelected.id === Currency.USDC">
            {{ quote ? (quote.amountOutNumber * 0.995).toFixed(6) : "0" }}
            <img class="rounded-full h-4" src="/images/usdc.svg" />
          </template>
          <template v-else-if="swapOutSelected.id !== Currency.USDC && amountOutput > 0">
            ~ {{ parseFloat(amountOutput.toFixed(3)) }}
            <img
              class="rounded-full h-4"
              :src="
                swapOutSelected.id === Currency.POL ? '/images/pol-token.svg' : '/images/wpol.svg'
              "
            />
          </template>
          <div class="text-zinc-400" v-else>-</div>
        </div>
      </div>
    </div>

    <div class="flex flex-col items-center mt-4 gap-4">
      <Button
        :disabled="!isAmountValid || amountToSwap === 0 || isLoading"
        :loading="loadingQuote"
        type="primary-blue"
        size="md"
        class="w-full"
        @click="swap"
      >
        <template v-if="isAmountValid === false">
          {{ t("_payment.insufficient-funds") }}
        </template>
        <template v-else> Swap </template>
      </Button>
    </div>
    <div v-if="errorMessage" class="text-red-500 text-sm mt-4 text-center">
      {{ errorMessage.message || errorMessage }}
    </div>
  </Modal>
</template>

<style scoped>
.arrow-container {
  transition: transform 0.1s ease-in-out;
}

.arrow-container.rotating {
  transform: translateX(-50%) translateY(-50%) rotate(-180deg);
}
</style>
